import { get } from 'lodash';

import {
  questionnaireLogicService,
  QuotaConditionTypeEnum,
  VALIDATION_ERRORS,
  VALIDATION_ERROR_LEVELS,
  VALIDATION_SCOPES,
} from 'utils/adl-services';

export const ADL_ERROR_SECTIONS = {
  ADVANCED_LOGIC: 'advancedLogic',
  DISPLAY_LOGIC: 'displayLogic',
  HIDDEN_VARIABLES: 'hiddenVariables',
  QUOTA_LOGIC: 'quotaLogic',
};

const SECTION_BY_SCOPE = {
  [VALIDATION_SCOPES.ANSWER]: ADL_ERROR_SECTIONS.DISPLAY_LOGIC,
  [VALIDATION_SCOPES.NODE]: ADL_ERROR_SECTIONS.DISPLAY_LOGIC,
  [VALIDATION_SCOPES.VARIABLE]: ADL_ERROR_SECTIONS.HIDDEN_VARIABLES,
};

const isAdvancedQuotaStatement = (statement) =>
  get(statement, 'rule.quota.context.type') === QuotaConditionTypeEnum.Advanced;

const toReferenceKey = (nodeId, hiddenAnswer) => `${nodeId}:${hiddenAnswer}`;

// Graph-scoped references come from both advanced logic and advanced quota
// rules (both use Success/Failure outcomes), so the QL is needed to tell them
// apart. Statements without a rule are kept because they carry the hide outcomes.
function getHiddenAnswerReferenceKeys(ql, keepStatement) {
  const nodeStatements = get(ql, 'nodeStatements', []).map((nodeStatement) => ({
    ...nodeStatement,
    statements: nodeStatement.statements.filter(
      (statement) => !statement.rule || keepStatement(statement),
    ),
  }));

  return new Set(
    questionnaireLogicService
      .getConditionsReferencingHiddenAnswers({ ...ql, nodeStatements })
      .map(({ nodeId, hiddenAnswer }) => toReferenceKey(nodeId, hiddenAnswer)),
  );
}

function getGraphHiddenAnswerSections(graphErrors, ql) {
  if (graphErrors.length === 0) {
    return [];
  }

  const advancedLogicKeys = getHiddenAnswerReferenceKeys(
    ql,
    (statement) => !statement.rule.quota,
  );
  const quotaLogicKeys = getHiddenAnswerReferenceKeys(
    ql,
    isAdvancedQuotaStatement,
  );

  return graphErrors.flatMap(({ nodeId, details }) => {
    const key = toReferenceKey(nodeId, get(details, 'hiddenAnswer'));
    const sections = [];
    if (advancedLogicKeys.has(key)) {
      sections.push(ADL_ERROR_SECTIONS.ADVANCED_LOGIC);
    }
    if (quotaLogicKeys.has(key)) {
      sections.push(ADL_ERROR_SECTIONS.QUOTA_LOGIC);
    }
    return sections;
  });
}

/**
 * Returns the sidebar sections (ADL_ERROR_SECTIONS) that contain
 * at least one blocking ADL validation error.
 */
export function getAdlBlockingErrorSections(errors = [], ql = {}) {
  const blockingErrors = errors.filter(
    ({ level }) => level === VALIDATION_ERROR_LEVELS.BLOCKING,
  );

  const sections = new Set();
  const graphHiddenAnswerErrors = [];

  blockingErrors.forEach((error) => {
    const { code, scope } = error;

    if (SECTION_BY_SCOPE[scope]) {
      sections.add(SECTION_BY_SCOPE[scope]);
    } else if (
      code === VALIDATION_ERRORS.HIDDEN_ANSWER_REFERENCE &&
      scope === VALIDATION_SCOPES.GRAPH
    ) {
      graphHiddenAnswerErrors.push(error);
    } else {
      sections.add(ADL_ERROR_SECTIONS.ADVANCED_LOGIC);
    }
  });

  getGraphHiddenAnswerSections(graphHiddenAnswerErrors, ql).forEach((section) =>
    sections.add(section),
  );

  return Object.values(ADL_ERROR_SECTIONS).filter((section) =>
    sections.has(section),
  );
}
